// Hoisted from the GLOBAL `Math` so hot loops below reference a module-scope
// binding. A global lookup is not inline-cacheable inside a `vm` context
// (how jest isolates modules), which made these loops ~100x slower under
// test than in production. Exactly semantics-preserving: every `Math`
// member is a pure static that never reads `this`.
const MATH_COS = Math.cos;
const MATH_SIN = Math.sin;
// shared-lib/geometry/objectFootprint.js
//
// THE footprint of a placed object: its four floor-plane corners, in the
// handedness the rest of this codebase stores rotations in.
//
// There are two conventions in the tree and they are mirror images:
//
//   calculateCorners(center, w, d, θ)   maps local +X to ( cos θ,  sin θ)
//   every builder's stored `rotation.y`  has local +X at ( cos ry, -sin ry)
//
// (`rotation.y = atan2(normal.x, normal.z)`, which is what `getWallAxes`
// computes.) So `calculateCorners(c, w, d, obj.rotation.y)` REFLECTS the
// rectangle about the object's own centre. The reflection maps a centred
// rectangle onto itself at 0/90/180/270, which is why orthogonal rooms never
// noticed — a 2 m x 0.6 m body at 30 degrees is out by up to 1.04 m, and at 45
// by 0.99 m. Note 45 is NOT a safe angle: it agrees only for a SQUARE
// footprint. Any test that pins this must use an oblique, non-square case.
//
// `calculateCorners(c, w, d, -obj.rotation.y)` is equivalent to this function
// and is the minimal fix at a call site; prefer this one, which cannot be
// passed the wrong sign at all.
//
// Winding matches `calculateCorners` / `calculateRectCorners`
// ([back-left, back-right, front-right, front-left]) so SAT sees a consistent
// convex polygon whichever helper produced the other operand.

const {
  calculateRectCorners,
} = require('../bathroom-analyzer/pipeline/geometry');

/**
 * @param {{center:{x,z}, dimensions:{x,z}, rotation?:{y}}} obj
 * @returns {{x:number,z:number}[]|null} four corners, or null when the object
 *   has no usable centre or a non-positive footprint.
 */
function objectFootprint(obj) {
  const c = obj?.center;
  const w = obj?.dimensions?.x;
  const d = obj?.dimensions?.z;
  if (!c || typeof c.x !== 'number' || typeof c.z !== 'number') return null;
  if (!(w > 0) || !(d > 0)) return null;
  const ry = obj.rotation?.y || 0;
  const dir = { x: MATH_COS(ry), z: -MATH_SIN(ry) };
  const normal = { x: MATH_SIN(ry), z: MATH_COS(ry) };
  return calculateRectCorners({ x: c.x, z: c.z }, w, d, dir, normal);
}

/**
 * The same footprint for something that carries its rotation as a bare scalar
 * in the stored convention (`rotationY`) rather than a `rotation` object.
 *
 * This is the DROP-IN for `calculateCorners(center, w, d, rotation.y)`, and it
 * keeps that helper's one lenient behaviour on purpose: a zero (or missing)
 * width or depth yields a degenerate rectangle, never null. The sweep that
 * introduced this file converted ~30 call sites, several of which feed a
 * scanned object's `dimensions.x || 0` straight in and then iterate the
 * corners — a null there is a TypeError inside a per-room try/catch, which is
 * how a whole bathroom's vanity assembly silently failed to build. Only a
 * centre that cannot be placed at all returns null. Callers that want the
 * strict "no usable footprint" answer use `objectFootprint`.
 */
function footprintFor(center, width, depth, rotationY = 0) {
  if (!center || typeof center.x !== 'number' || typeof center.z !== 'number') {
    return null;
  }
  const w = Number.isFinite(width) ? width : 0;
  const d = Number.isFinite(depth) ? depth : 0;
  const ry = rotationY || 0;
  const dir = { x: MATH_COS(ry), z: -MATH_SIN(ry) };
  const normal = { x: MATH_SIN(ry), z: MATH_COS(ry) };
  return calculateRectCorners({ x: center.x, z: center.z }, w, d, dir, normal);
}

/**
 * The 6-corner footprint of an L-shaped corner cabinet, in the same handedness
 * as `objectFootprint`.
 *
 * The L is a rectangle with one quadrant missing: `alongA` along the object's
 * local +X, `alongB` along local +Z, and a square `cornerBox` of overlap at the
 * wall corner. Walking it from that corner:
 *
 *     6 ______ 5           1  wall corner        4  inner corner
 *      |      |            2  end of leg A       5  front of leg B
 *      |   4__|3           3  front of leg A     6  end of leg B
 *      |___|
 *     1     2
 *
 * Callers that draw this polygon had each re-derived it, and `roomTakeOffs.js`
 * got the rotation handedness backwards — it applied R(+ry) where every builder
 * stores local +X at `(cos ry, −sin ry)`, so its vertices came out rotated by
 * 2·ry. At ry = 90° that is a 180° flip about the centre, putting the drawn
 * outline up to 1.29 m (50.9") from the real one. Areas were unaffected (the
 * AABB is symmetric), so it only ever showed up as a wrong SHAPE in the floor
 * SVG, the schedule PDF, the take-off overlay and the UV plan.
 *
 * @param {{center:{x,z}, dimensions:{alongA,alongB,cornerBox}, rotation?:{y}}} obj
 * @returns {{x:number,z:number}[]|null} six corners, or null when the object is
 *   not a usable L.
 */
function lFootprint(obj) {
  const c = obj?.center;
  const alongA = obj?.dimensions?.alongA;
  const alongB = obj?.dimensions?.alongB;
  const cornerBox = obj?.dimensions?.cornerBox;
  if (!c || typeof c.x !== 'number' || typeof c.z !== 'number') return null;
  if (!(alongA > 0) || !(alongB > 0) || !(cornerBox > 0)) return null;
  const hx = alongA / 2;
  const hz = alongB / 2;
  const cb = Math.min(cornerBox, alongA, alongB);
  const ry = obj.rotation?.y || 0;
  const cos = Math.cos(ry);
  const sin = Math.sin(ry);
  return [
    [-hx, -hz],
    [hx, -hz],
    [hx, -hz + cb],
    [-hx + cb, -hz + cb],
    [-hx + cb, hz],
    [-hx, hz],
  ].map(([lx, lz]) => ({
    x: c.x + lx * cos + lz * sin,
    z: c.z - lx * sin + lz * cos,
  }));
}

module.exports = { objectFootprint, footprintFor, lFootprint };
